import { Fragment } from "react";
import { Pencil, Trash2 } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { IconButton } from "@/components/ui/IconButton";
import { cn } from "@/lib/cn";
import { MeasurementRow } from "@/types/measurements";

const UTILITY_LABELS: Record<string, string> = {
  "zimna-woda":         "Zimna woda",
  "podgrzanie-cw":      "Ciepła woda",
  "oplata-stała-co-cw": "Opłata stała CO CW",
  "en-el":              "Prąd",
  "gaz":                "Gaz",
  "co":                 "CO",
  "smieci":             "Śmieci",
  "internet":           "Internet",
};

const LOCATION_LABELS: Record<string, string> = {
  krasinskiego: "Krasińskiego",
  stanczyka:    "Stańczyka",
  rydlowka:     "Rydlówka",
};

const USAGE_UNIT: Record<string, string> = {
  "zimna-woda":    "m³",
  "podgrzanie-cw": "m³",
  "co":            "GJ",
  "en-el":         "kWh",
  "gaz":           "m³",
};

function shortDate(iso: string | null) {
  if (!iso) return "—";
  return iso.slice(5, 10).replace("-", ".");
}

interface Props {
  rows: MeasurementRow[];
  onEdit?: (row: MeasurementRow) => void;
  onDelete?: (row: MeasurementRow) => void;
}

export function MeasurementsGrid({ rows, onEdit, onDelete }: Props) {
  const byLocation = new Map<string, MeasurementRow[]>();
  for (const r of rows) {
    const arr = byLocation.get(r.location) ?? [];
    arr.push(r);
    byLocation.set(r.location, arr);
  }

  const multipleLocations = byLocation.size > 1;

  function usageCell(r: MeasurementRow) {
    if (r.usage) {
      return <>{r.usage} <span className="text-text-muted text-xs">{USAGE_UNIT[r.utility] ?? ""}</span></>;
    }
    if (r.editable && r.value) {
      return <span className="text-text-muted">{r.value} <span className="text-xs">{r.unit}</span></span>;
    }
    return <span className="text-text-subtle">—</span>;
  }

  function amountCell(r: MeasurementRow) {
    if (r.amount) return <>{r.amount} <span className="text-text-muted text-xs">PLN</span></>;
    return <span className="text-text-subtle">—</span>;
  }

  function settlementLine(r: MeasurementRow) {
    if (!r.settlement) return null;
    return (
      <>
        {r.settlement.description || "Rozliczenie szacunku"}:{" "}
        <span className="font-mono">{r.settlement.amount} PLN</span>
      </>
    );
  }

  return (
    <>
      {/* Desktop — single table, location group header rows guarantee column alignment */}
      <Card padding="none" className="hidden lg:block">
        <table className="w-full text-sm">
          <thead className="text-xs text-text-muted">
            <tr className="border-b border-border">
              <th className="text-left font-medium px-4 py-2">Media</th>
              <th className="text-right font-medium px-4 py-2">Zużycie</th>
              <th className="text-right font-medium px-4 py-2">Kwota</th>
              <th className="text-left font-medium px-4 py-2">Źródło</th>
              <th className="text-left font-medium px-4 py-2">Modyfikacja</th>
              <th className="px-4 py-2 w-20"></th>
            </tr>
          </thead>
          <tbody>
            {Array.from(byLocation.entries()).map(([loc, locRows]) => (
              <>
                {/* Location group header — only when showing multiple locations */}
                {multipleLocations && (
                  <tr key={`hdr-${loc}`} className="border-b border-border">
                    <td colSpan={6} className="px-4 py-1.5 text-xs font-semibold text-text-muted uppercase tracking-wide bg-bg-subtle capitalize">
                      {LOCATION_LABELS[loc] ?? loc}
                    </td>
                  </tr>
                )}
                {locRows.map((r) => (
                  <Fragment key={`${r.location}/${r.utility}`}>
                  <tr className={cn(
                    "border-b border-border last:border-0",
                    r.missing && "bg-warning/5",
                  )}>
                    <td className="px-4 py-2 text-xs">{UTILITY_LABELS[r.utility] ?? r.utility}</td>
                    <td className="px-4 py-2 text-xs text-right font-mono">{usageCell(r)}</td>
                    <td className="px-4 py-2 text-xs text-right font-mono">{amountCell(r)}</td>
                    <td className="px-4 py-2 text-xs text-text-muted">{r.source ?? "—"}</td>
                    <td className="px-4 py-2 text-xs text-text-muted font-mono">{shortDate(r.last_modified)}</td>
                    <td className="px-4 py-2 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {onEdit && (
                          <IconButton label="Edytuj" onClick={() => onEdit(r)}><Pencil className="size-3.5" /></IconButton>
                        )}
                        {onDelete && (
                          <IconButton label="Usuń" onClick={() => onDelete(r)}><Trash2 className="size-3.5" /></IconButton>
                        )}
                      </div>
                    </td>
                  </tr>
                  {r.settlement && (
                    <tr className="border-b border-border last:border-0">
                      <td colSpan={6} className="px-4 pb-2 pl-8 text-xs text-text-muted">
                        ↳ {settlementLine(r)}
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
              </>
            ))}
          </tbody>
        </table>
      </Card>

      {/* Mobile — separate cards per location (natural on small screens) */}
      <div className="lg:hidden flex flex-col gap-3">
        {Array.from(byLocation.entries()).map(([loc, locRows]) => (
          <Card key={loc} padding="default">
            {multipleLocations && (
              <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wide capitalize mb-2">
                {LOCATION_LABELS[loc] ?? loc}
              </h3>
            )}
            <table className="w-full text-xs">
              <tbody>
                {locRows.map((r) => (
                  <Fragment key={r.utility}>
                  <tr className={cn(r.missing && "text-warning")}>
                    <td className="text-text-muted py-1">{UTILITY_LABELS[r.utility] ?? r.utility}</td>
                    <td className="py-1 text-right font-mono">{usageCell(r)}</td>
                    <td className="py-1 text-right font-mono pl-2">{amountCell(r)}</td>
                    <td className="py-1 text-right pl-2">
                      <div className="flex items-center justify-end gap-1">
                        {onEdit && (
                          <button onClick={() => onEdit(r)} className="text-text-muted hover:text-text">
                            <Pencil className="size-3 inline" />
                          </button>
                        )}
                        {onDelete && (
                          <button onClick={() => onDelete(r)} className="text-text-muted hover:text-danger">
                            <Trash2 className="size-3 inline" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {r.settlement && (
                    <tr>
                      <td colSpan={4} className="pb-1 pl-3 text-text-muted">↳ {settlementLine(r)}</td>
                    </tr>
                  )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </Card>
        ))}
      </div>
    </>
  );
}
